import { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';
import { useProcContext } from '@/entities/proc';
import {
  ProcsComponentsRenderer,
  useProcManageActions,
} from '@/features/proc-management';
import { ComponentResponse, StepItemResponse } from '@/shared/api';
import { componentsInitialValues } from '@/shared/config';
import { themeConfig, useThemeContext } from '@/shared/theme';
import {
  EventTypes,
  DndItemType,
  ComponentItemType,
  ComponentMode as Mode,
  ChatMode,
} from '@/shared/types';
import { on, off } from '@/shared/utils';
import { InstanceStepChat } from '@/widgets/chat';

const reorder = (
  list: ComponentResponse[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const EditProcViewCanvas = () => {
  const { theme } = useThemeContext();
  const { proc, setSelectedComponent, selectedStep, commentsLabel } =
    useProcContext();

  const [stepComponents, setStepComponents] = useState(
    selectedStep?.components || [],
  );
  const { addComponentToStep, updateStep } = useProcManageActions();

  const addComponentToCanvas = useCallback(
    async (id: ComponentItemType['id']) => {
      const component =
        componentsInitialValues[id as keyof typeof componentsInitialValues] ||
        {};
      const lastComponent = selectedStep?.components?.findLast(
        (cmp: ComponentResponse) => cmp.component === id,
      );
      const lastComponentLabelNum = +lastComponent?.componentName?.charAt(
        lastComponent?.componentName?.length - 1,
      );
      component.componentName = `${id}${lastComponentLabelNum + 1 || 1}`;

      addComponentToStep({
        component,
        stepId: selectedStep?.id || '',
        onSuccess: (component) => setSelectedComponent(component),
      });
    },
    [
      addComponentToStep,
      selectedStep?.components,
      selectedStep?.id,
      setSelectedComponent,
    ],
  );

  // react-dnd
  const [, drop] = useDrop<any>(
    () => ({
      accept: DndItemType.ComponentItem,
      drop: (item) => addComponentToCanvas(item.id),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        item: monitor.getItem(),
      }),
    }),
    [addComponentToCanvas],
  );

  const handleComponentsReorder = async (
    components: ComponentResponse[] = [],
  ) => {
    if (selectedStep?.id) {
      const componentsIds = components.map((component) => component.id);
      updateStep({
        stepId: selectedStep.id,
        data: { componentsIds },
      });
    }
  };

  function onDragEnd(result: any) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const items = reorder(
      [...stepComponents],
      result.source.index,
      result.destination.index,
    );
    setStepComponents(items);
    handleComponentsReorder(items);
  }

  useEffect(() => {
    setStepComponents(selectedStep?.components || []);
  }, [selectedStep]);

  useEffect(() => {
    const handler = (event: any) => {
      addComponentToCanvas(event.detail.id);
    };
    on(EventTypes.COMPONENT_ADDED_TO_CANVAS, handler);

    return () => off(EventTypes.COMPONENT_ADDED_TO_CANVAS, handler);
  }, [addComponentToCanvas]);

  return (
    <Wrapper
      data-testid="canvas"
      ref={drop}
      onClick={() => setSelectedComponent(undefined)}
      style={{ background: themeConfig[theme].colors.layoutBg }}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable-canvas-components">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {stepComponents.map((block: ComponentResponse, index: number) =>
                ProcsComponentsRenderer({
                  block,
                  mode: Mode.Edit,
                  index,
                }),
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {selectedStep &&
        (selectedStep.commentsEnabled ||
          proc?.stepData?.administration?.aiAssistantEnabled) && (
          <div style={{ padding: '1rem', width: '100%', marginTop: 'auto' }}>
            <InstanceStepChat
              step={selectedStep as StepItemResponse}
              label={commentsLabel}
              chatMode={ChatMode.Preview}
              chatEnabled={selectedStep?.commentsEnabled}
              aiAssistantEnabled={
                proc?.stepData?.administration?.aiAssistantEnabled
              }
              postedBy="userAccount"
            />
          </div>
        )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  max-width: 736px;
  height: 100%;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 40px;
`;
